import React from 'react'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import ClickOutside from 'rechat-react-click-outside'
import styled from 'styled-components'
import DcDiv from '../dc-div/dc-div'
import DcIcon from '../dc-icon/dc-icon'
import PixelText from '../pixel-text/pixel-text'
import { $DCprimaryActiveColor, $DCprimaryColor, colors } from '../styleGuide'

export interface DcInputTagProps {
  className?: string
  tags?: InputOption[]
  onTagUpdate?: any
  options?: InputOption[]
  placeholder?: string
  allowCustomTags?: boolean
  handleTagDelete?: any
  handleTagAdd?: any
  clearAll?: any
  noDataText?: string
  isAllClearable?: boolean
  isShowLabel?: boolean
  inputLabel?: string
  error?: string
  size?: string
  showAddButton?: boolean
}
export interface InputOption {
  label: string
  value: string
}
const StyledPixelInputTag = styled.div<{ size?: string }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  padding-left: ${(props) => (props.size === 'sm' ? '10px' : '20px')};
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: ${colors.grayBorderLight} transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.grayBorderLight};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${colors.grayBorder};
  }
`
const TagInput = styled.input<{ $size?: string }>`
  /* min-height: 2.75rem; */
  min-height: ${(props) => (props.$size === 'sm' ? '30px' : '44px')};
  border: none;
  outline: none;
  background-color: inherit;
  &.invalid {
    color: red;
  }
`

const Tags = styled.div<{ size?: string }>`
  margin: ${(props) =>
    props.size === 'sm' ? '2px 4px' : '5px 4px'} !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  background: ${$DCprimaryColor};
  font-size: ${(props) => (props.size === 'sm' ? '10px' : '12px')} !important;
  padding: ${(props) =>
    props.size == 'sm' ? '2px 10px 3px 10px' : '3px 10px 5px 10px'};
  border-radius: 16px;
  color: white;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`
export const DcInputTag = React.forwardRef<HTMLDivElement, DcInputTagProps>(
  (props, ref) => {
    const {
      className,
      tags = [],
      onTagUpdate = () => {},
      options = [],
      placeholder,
      handleTagDelete = () => {},
      handleTagAdd = () => {},
      allowCustomTags = true,
      clearAll = () => {},
      noDataText = 'No data found',
      isAllClearable = true,
      isShowLabel = true,
      inputLabel = '',
      error = '',
      size,
      showAddButton,
      ...rest
    } = props
    const [filterText, setFilterText] = React.useState('')
    const [localTags, setLocalTags] = React.useState(tags)
    const [isOptionsOpen, setIsOptionsOpen] = React.useState(false)
    const inputTagref = React.useRef(null)
    const optionsref = React.useRef(null)
    const [invalid, setInvalid] = React.useState(false)

    const handleDelete = (value: string) => {
      setLocalTags(localTags.filter((t) => t.value !== value))
      handleTagDelete &&
        handleTagDelete({
          label: localTags.find((t) => t.value === value).label,
          value: value,
        })
    }
    const handleSearch = (key: 'Enter') => {
      if (key === 'Enter') {
        if (filterText)
          if (allowCustomTags) {
            if (localTags.find((t) => t.label.toLowerCase() === filterText)) {
              return
            } else {
              const value = inputTagref.current.value
              const tag = {
                label: value,
                value: value,
              }
              setLocalTags([...localTags, tag])
              handleTagAdd(tag)
              setFilterText('')
            }
          } else {
            if (localTags.find((t) => t.label.toLowerCase() === filterText)) {
              return
            } else {
              if (options.find((t) => t.label.toLowerCase() === filterText)) {
                const value = inputTagref.current.value
                setLocalTags([...localTags, { label: value, value: value }])
                setFilterText('')
              }
            }
          }
      }
    }
    const addCurrentFilter = () => {
      if (!filterText) return
      if (allowCustomTags) {
        if (
          localTags.find(
            (t) => t.label.toLowerCase() === filterText.toLowerCase(),
          )
        )
          return
        const value = inputTagref.current.value
        const tag = { label: value, value: value }
        setLocalTags([...localTags, tag])
        handleTagAdd(tag)
        setFilterText('')
        return
      }
      const match = options.find(
        (t) => t.label.toLowerCase() === filterText.toLowerCase(),
      )
      if (!match) return
      if (localTags.find((t) => t.value === match.value)) return
      const tag = { label: match.label, value: match.value }
      setLocalTags([...localTags, tag])
      handleTagAdd(tag)
      setFilterText('')
    }

    const handleOptionClick = (option: InputOption) => {
      if (localTags.find((t) => t.value === option.value)) {
        return
      } else {
        const newTags = [...localTags, option]
        setLocalTags(newTags)
        setIsOptionsOpen(false)
        setFilterText('')
        onTagUpdate(newTags)
        handleTagAdd &&
          handleTagAdd({
            label: option.label,
            value: option.value,
          })
      }

      inputTagref.current.focus()
    }

    const toggleOptions = () => {
      setIsOptionsOpen(true)
    }

    const filterData = (options: InputOption[], filterText: string) => {
      if (filterText) {
        return options.filter((option) =>
          option.label.toLowerCase().includes(filterText.toLowerCase()),
        )
      }
      return options
    }

    React.useEffect(() => {
      if (tags.length) setLocalTags(tags)
      return () => {
        setLocalTags([])
      }
    }, [tags])

    React.useEffect(() => {
      if (filterText.length > 0) {
        setIsOptionsOpen(true)
      }
    }, [filterText])
    const truncate = (label: string, max: number) => {
      return label?.length > max ? label.substr(0, max - 1) + '...' : label
    }
    return (
      <ClickOutside
        style={{ width: '100%' }}
        onClickOutside={() => setIsOptionsOpen(false)}
      >
        <Container size={size} tabIndex={0}>
          <StyledPixelInputTag
            size={size}
            onClick={() => {
              inputTagref.current.focus()
              toggleOptions()
            }}
          >
            {inputLabel && (
              <StyledLabel className={'showLabell'}>
                {truncate(inputLabel, 25)}
              </StyledLabel>
            )}
            {localTags.length > 0 && (
              <React.Fragment>
                {localTags?.map((tag, i) => {
                  return (
                    <Tags size={size} key={i}>
                      {tag.label} &nbsp;
                      <DcDiv
                        color="white"
                        hoverStyle={{ color: colors.inputTagBlue }}
                        width={size == 'sm' ? '7px' : '12px'}
                      >
                        <DcIcon
                          color="inherit"
                          width={'10px'}
                          icon={faXmark}
                          fontSize="12px"
                          onClick={() => {
                            handleDelete(tag.value)

                            setIsOptionsOpen(false)
                          }}
                        />
                      </DcDiv>
                    </Tags>
                  )
                })}
              </React.Fragment>
            )}

            <TagInput
              $size={size}
              onBlur={() => {}}
              placeholder={placeholder}
              className={invalid ? 'invalid' : ''}
              value={filterText || ''}
              ref={inputTagref}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSearch('Enter')
                }
              }}
              onChange={(e) => {
                setFilterText(e.target.value)
                if (e.target.value !== '') {
                  const isInvalid = options.find((item) => {
                    return (
                      item.label.toLowerCase() === e.target.value.toLowerCase()
                    )
                  })
                  if (isInvalid) {
                    setInvalid(false)
                  } else {
                    setInvalid(true)
                  }
                }
              }}
            />
            {showAddButton && (
              <AddButton
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  addCurrentFilter()
                }}
                disabled={!filterText || invalid}
              >
                Add
              </AddButton>
            )}
          </StyledPixelInputTag>
          {isOptionsOpen && (
            <DropDownList>
              {filterData(options, filterText).length > 0 ? (
                <React.Fragment>
                  {filterData(options, filterText).map((option, i) => {
                    return (
                      <Option
                        className={
                          localTags.find((tag) => tag.value === option.value)
                            ? 'selected'
                            : ''
                        }
                        ref={optionsref}
                        key={i}
                        onClick={() => {
                          handleOptionClick(option)
                        }}
                      >
                        {option.label}
                      </Option>
                    )
                  })}
                </React.Fragment>
              ) : (
                <Option>{noDataText && noDataText}</Option>
              )}
            </DropDownList>
          )}
          {isAllClearable && (
            <React.Fragment>
              {localTags.length > 0 && (
                <ClearAll
                  onClick={() => {
                    setLocalTags([])
                    clearAll && clearAll()
                  }}
                >
                  <DcIcon color="inherit" fontSize="18px" icon={faXmark} />
                </ClearAll>
              )}
            </React.Fragment>
          )}
        </Container>
        {error !== '' && (
          <PixelText customColor={'rgb(255 0 0 / 64%)'}>{error}</PixelText>
        )}
      </ClickOutside>
    )
  },
)
const AddButton = styled.button`
  position: absolute;
  right: 8px;
  top: 8px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid ${colors.grayBorderLight};
  background: ${colors.white};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
const ClearAll = styled.div`
  width: 12px;
  right: 7px;
  top: 10px;
  position: absolute;
  display: none;
  margin-right: 20px;
  color: ${colors.grayBorderLight};
  &:hover {
    color: ${$DCprimaryActiveColor};
  }
`
const Container = styled.div<{ size?: string }>`
  position: relative;
  min-height: ${(props) => (props.size === 'sm' ? '30px' : '44px')} !important;
  max-height: ${(props) => (props.size === 'sm' ? '30px' : '44px')} !important;
  border-radius: 4px;
  transition: border-color 0.5s ease-in-out, box-shadow 0.5s ease-in-out;
  background-color: ${colors.white};
  border: 0.0625rem solid ${colors.grayBorderLight};
  color: ${colors.grayInputDark};
  appearance: none;
  background-clip: padding-box;
  border-radius: 0.375rem;
  display: block;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: text;
  position: relative;
  width: 100%;
  overflow: hidden;
  /* padding: 0.5625rem 1.125rem !important; */
  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
    border: 1px solid ${$DCprimaryColor};
    box-shadow: 0 0 0 1px ${$DCprimaryColor} !important;
  }
  border-radius: 4px;
  &:hover {
    ${ClearAll} {
      display: block;
    }
  }
`

const DropDownList = styled.div`
  background-color: ${colors.white};
  border: 1px solid ${colors.dropdownBorderLight};
  border-top: none;
  width: 100%;
  min-width: 300px;
  max-height: 300px;
  overflow: auto;

  padding-top: 6px;
  position: absolute;
  z-index: 99;
`
const Option = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  background-color: ${colors.white};
  min-width: 100%;
  height: 40px;
  padding: 5px 5px 5px 25px;
  cursor: pointer;
  &:hover {
    background-color: ${colors.blueLightBg};
  }
  &.selected {
    cursor: not-allowed;
    background-color: ${$DCprimaryColor};
    color: ${colors.white};
  }
`
const StyledLabel = styled.div`
  &.showLabell {
    background: linear-gradient(180deg, ${colors.white} 52%, transparent 48%);
    top: -9px;
    opacity: 1;
    left: 13px;
    z-index: 1;
  }
  font-weight: 400;
  opacity: 0;
  color: ${colors.datePickerGray};
  position: absolute;
  width: auto;
  top: -10px;
  left: '15px';
  font-size: 11px;
  transition: all 0.2s ease-in-out;
  border-radius: 4px !important;
`

export default DcInputTag
